import React, { useEffect, useState, useCallback } from "react";
import { formatDate, segmentColor, statusColor } from "../../lib/format";
import { Phone, Calendar, Beer, Plus, Minus, Gift, Stamp, Check, Ticket, X } from "lucide-react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

export function CustomerProfileCard({ detail, onRefresh, onClear }) {
  const { hasFeature } = useAuth();
  const bottleLockerOn = hasFeature("bottle_locker");

  if (!detail) {
    return (
      <div className="bg-[#1A1A1A] border border-dashed border-white/10 rounded-xl p-8 text-center">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#2D1B4E] flex items-center justify-center">
          <Beer size={24} className="text-[#9D00FF]" />
        </div>
        <div className="text-white font-semibold mb-1">No customer selected</div>
        <p className="text-sm text-[#A0A0A0]">
          Search by phone or name above to pull up a customer profile, or add a new one.
        </p>
      </div>
    );
  }

  const { customer, active_tabs = [], active_tabs_count = 0, available_rewards_count = 0 } = detail;
  const bottleCount = active_tabs_count || active_tabs.length;
  return (
    <div className="flex flex-col gap-4 tb-fade-up">
      {/* ---- Customer Profile Header ---- */}
      <div className="bg-gradient-to-br from-[#2D1B4E] to-[#1A1A1A] border border-[#9D00FF]/30 rounded-xl p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-white truncate" data-testid="customer-name">
              {customer.name}
            </h2>
            <div className="text-sm text-[#A0A0A0] flex items-center gap-1 mt-1" data-testid="customer-phone">
              <Phone size={12} /> {customer.phone}
            </div>
            {customer.dob && (
              <div className="text-xs text-[#A0A0A0] flex items-center gap-1 mt-0.5">
                <Calendar size={11} /> DOB {formatDate(customer.dob)}
              </div>
            )}
            {!customer.dob && <DatePrompt customerId={customer.id} field="dob" label="Date of birth" onSaved={onRefresh} />}
            {customer.anniversary && (
              <div className="text-xs text-[#A0A0A0] flex items-center gap-1 mt-0.5">
                <Calendar size={11} /> Anniversary {formatDate(customer.anniversary)}
              </div>
            )}
            {!customer.anniversary && <DatePrompt customerId={customer.id} field="anniversary" label="Anniversary" onSaved={onRefresh} />}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span
              data-testid="customer-segment"
              className={`text-[10px] px-2 py-1 rounded-md border font-bold uppercase tracking-wider ${segmentColor(
                customer.segment
              )}`}
            >
              {customer.segment}
            </span>
            {onClear && (
              <button
                type="button"
                onClick={onClear}
                data-testid="customer-profile-clear"
                className="w-11 h-11 flex items-center justify-center rounded-lg text-[#A0A0A0] hover:text-white hover:bg-white/10 transition"
                title="Clear customer"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Visits</div>
            <div className="text-xl font-bold text-white" data-testid="customer-visits">
              {customer.total_visits}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Last visit</div>
            <div className="text-sm font-semibold text-white" data-testid="customer-last-visit">
              {customer.last_visit_at ? formatDate(customer.last_visit_at) : "—"}
            </div>
          </div>
          {bottleLockerOn && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] flex items-center gap-1">
                <Beer size={10} /> Active bottles
              </div>
              <div className="text-xl font-bold text-[#9D00FF]" data-testid="customer-bottle-count">
                {bottleCount}
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] flex items-center gap-1">
              <Gift size={10} /> Rewards
            </div>
            <div className="text-xl font-bold text-[#9D00FF]" data-testid="customer-rewards-count">
              {available_rewards_count}
            </div>
          </div>
        </div>
      </div>

      {/* ---- Loyalty Points + Redeem + Milestones ---- */}
      <LoyaltyPointsCard customer={customer} onRefresh={onRefresh} />

      {/* ---- Tabs (only shown when bottle_locker is enabled) ---- */}
      {bottleLockerOn && (
        <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Beer size={16} className="text-[#9D00FF]" />
            <h3 className="font-semibold">Active Tabs</h3>
            <span className="text-xs text-[#A0A0A0]">({active_tabs.length})</span>
          </div>
          {active_tabs.length === 0 ? (
            <p className="text-sm text-[#A0A0A0]">No active bottle tabs.</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {active_tabs.map((t) => (
                <div
                  key={t.id}
                  data-testid={`active-tab-${t.id}`}
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 flex items-center justify-between"
                >
                  <div>
                    <div className="font-semibold text-white text-sm">{t.brand}</div>
                    <div className="text-xs text-[#A0A0A0]">
                      {t.remaining_ml}ml left · Expires {formatDate(t.expiry_at)}
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-md border uppercase tracking-wider font-bold ${statusColor(t.status)}`}>
                    {t.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- Rewards ---- */}
      <RewardsWallet
        customerId={customer.id}
        customerName={customer.name}
        onDone={onRefresh}
      />
    </div>
  );
}


function LoyaltyActions({ customerId, balance, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(50);
  const [amountInr, setAmountInr] = useState(0);
  const [mode, setMode] = useState("redeem"); // redeem | cover | bonus
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (amount === 0) return;
    let payload;
    if (mode === "redeem") {
      payload = { delta: -Math.abs(amount), reason: "redeem" };
    } else if (mode === "cover") {
      payload = { delta: Math.abs(amount), type: "cover", reason: "cover", amount_inr: amountInr };
    } else {
      payload = { delta: Math.abs(amount), type: "bonus", reason: "bonus" };
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/customers/${customerId}/points`, payload);
      if (mode === "redeem") {
        toast.success(`Redeemed ${amount} pts · new balance ${data.points_balance}`);
      } else if (mode === "cover") {
        toast.success(`Cover of ₹${amountInr} loaded as ${amount} pts`);
      } else {
        toast.success(`Added ${amount} pts · new balance ${data.points_balance}`);
      }
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const modeBtn = (m, label, icon) => (
    <button
      onClick={() => setMode(m)}
      data-testid={`loyalty-mode-${m}`}
      className={`py-2.5 rounded-lg text-sm font-semibold border flex items-center justify-center gap-1 ${
        mode === m
          ? "bg-[#9D00FF] border-[#9D00FF] text-white"
          : "bg-[#0A0A0A] border-white/10 text-[#A0A0A0]"
      }`}
    >
      {icon} {label}
    </button>
  );

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="loyalty-manage-button"
        className="bg-[#2D1B4E] border border-[#9D00FF]/40 hover:border-[#9D00FF] rounded-md px-3 py-1.5 text-xs font-semibold text-white"
      >
        Manage
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl w-full max-w-sm p-6 tb-fade-up">
            <h2 className="text-xl font-bold mb-1">Adjust Points</h2>
            <p className="text-xs text-[#A0A0A0] mb-4">Current balance: <span className="text-[#9D00FF] font-bold">{balance}</span></p>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {modeBtn("redeem", "Redeem", <Minus size={14} />)}
              {modeBtn("cover", "Cover", <Plus size={14} />)}
              {modeBtn("bonus", "+ Bonus", <Plus size={14} />)}
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <div className="text-xs text-[#A0A0A0] mb-1">Points to add</div>
                <input
                  data-testid="loyalty-amount-input"
                  type="number"
                  min="1"
                  value={amount}
                  onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-center text-2xl font-bold focus:border-[#9D00FF]"
                />
              </div>
              {mode === "cover" && (
                <div>
                  <div className="text-xs text-[#A0A0A0] mb-1">Cover Amount (₹)</div>
                  <input
                    data-testid="loyalty-amount-inr-input"
                    type="number"
                    min="0"
                    value={amountInr}
                    onChange={(e) => setAmountInr(Math.max(0, Number(e.target.value) || 0))}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-center text-2xl font-bold focus:border-[#9D00FF]"
                  />
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setOpen(false)} className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg py-2.5 min-h-[44px] font-semibold">Cancel</button>
              <button
                onClick={submit}
                disabled={loading || (mode === "redeem" && amount > balance)}
                data-testid="loyalty-submit"
                className="flex-1 tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 rounded-lg py-2.5 min-h-[44px] font-semibold"
              >
                {loading ? "…" : mode === "redeem" ? "Redeem" : mode === "cover" ? "Load Cover" : "Add Bonus"}
              </button>
            </div>
            {mode === "redeem" && amount > balance && (
              <p className="text-xs text-[#EF4444] mt-2 text-center">Not enough points</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ---------------- Rewards Wallet ----------------
export function RewardsWallet({ customerId, customerName, onDone }) {
  const { hasFeature } = useAuth();
  const stampCardsEnabled = hasFeature("stamp_cards");
  const [data, setData] = useState({ rewards: [], stamp_cards: [] });
  const [showGrant, setShowGrant] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers/${customerId}/rewards`);
      setData(data);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const addStamp = async (programId) => {
    try {
      const { data: res } = await api.post("/stamps", {
        customer_id: customerId, program_id: programId, count: 1,
      });
      if (res.rewards_created?.length) {
        toast.success(`Card complete! Earned: ${res.rewards_created[0].title}`);
      } else {
        toast.success("Stamp added");
      }
      load();
      onDone?.();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const redeem = async (rid) => {
    try {
      await api.post(`/rewards/${rid}/redeem`);
      toast.success("Reward redeemed");
      load();
      onDone?.();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const available = (data.rewards || []).filter((r) => r.status === "available");
  const used = (data.rewards || []).filter((r) => r.status !== "available");

  return (
    <>
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Gift size={16} className="text-[#9D00FF]" />
          <h3 className="font-semibold">Rewards</h3>
          <span className="text-xs text-[#A0A0A0]">({available.length} ready)</span>
          <button
            onClick={() => setShowGrant(true)}
            data-testid="grant-reward-button"
            className="ml-auto text-xs text-[#9D00FF] font-semibold hover:underline"
          >
            + Grant
          </button>
        </div>

        {/* Available rewards */}
        {available.length === 0 && (!stampCardsEnabled || (data.stamp_cards || []).length === 0) && (
          <p className="text-sm text-[#A0A0A0]">
            {stampCardsEnabled
              ? <>No rewards or stamp cards yet. Admin can set up Stamp Programs under <span className="text-white">Rewards</span>.</>
              : "No rewards yet."}
          </p>
        )}

        {available.length > 0 && (
          <div className="flex flex-col gap-2 mb-4">
            {available.map((r) => (
              <div key={r.id} data-testid={`reward-${r.id}`} className="bg-gradient-to-br from-[#2D1B4E] to-[#1A1A1A] border border-[#9D00FF]/40 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#D78CFF] font-bold mb-0.5">
                      <Ticket size={11} /> {r.source === "stamp" ? "Stamp Reward" : r.source === "offer" ? "Offer" : "Granted"}
                    </div>
                    <div className="font-bold text-white text-sm">{r.title}</div>
                    {r.description && <div className="text-xs text-[#A0A0A0] mt-0.5">{r.description}</div>}
                    {r.expires_at && <div className="text-[10px] text-[#F59E0B] mt-1">Expires {formatDate(r.expires_at)}</div>}
                  </div>
                  <button
                    onClick={() => redeem(r.id)}
                    data-testid={`redeem-reward-${r.id}`}
                    className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-xs font-bold px-3 py-2 rounded-md flex items-center gap-1"
                  >
                    <Check size={12} /> Redeem
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stamp cards — only shown when stamp_cards feature is enabled */}
        {stampCardsEnabled && (data.stamp_cards || []).length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] font-bold">Stamp Cards</div>
            {data.stamp_cards.map((c) => (
              <StampCardRow key={c.program_id} card={c} onAdd={() => addStamp(c.program_id)} />
            ))}
          </div>
        )}

        {used.length > 0 && (
          <details className="mt-4">
            <summary className="text-xs text-[#A0A0A0] cursor-pointer hover:text-white">
              History ({used.length})
            </summary>
            <div className="flex flex-col gap-1 mt-2">
              {used.slice(0, 10).map((r) => (
                <div key={r.id} className="text-xs flex items-center justify-between py-1 border-b border-white/5 last:border-0">
                  <span className="text-[#A0A0A0]">{r.title}</span>
                  <span className={`uppercase font-bold ${
                    r.status === "redeemed" ? "text-emerald-400" :
                    r.status === "expired" ? "text-[#F59E0B]" : "text-[#A0A0A0]"
                  }`}>{r.status}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {showGrant && (
        <GrantRewardDialog
          customerId={customerId}
          customerName={customerName}
          onClose={() => setShowGrant(false)}
          onGranted={() => { setShowGrant(false); load(); onDone?.(); }}
        />
      )}
    </>
  );
}

function StampCardRow({ card, onAdd }) {
  const pct = Math.min(100, (card.stamps_earned / card.required_stamps) * 100);
  const dots = Array.from({ length: card.required_stamps }, (_, i) => i < card.stamps_earned);
  return (
    <div data-testid={`stamp-card-${card.program_id}`} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-white text-sm truncate">{card.program_name}</div>
          <div className="text-[11px] text-[#A0A0A0]">Reward: {card.reward_title}</div>
        </div>
        <button
          onClick={onAdd}
          data-testid={`add-stamp-${card.program_id}`}
          disabled={!card.active}
          className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-40 text-white text-xs font-bold px-3 py-2 rounded-md flex items-center gap-1 whitespace-nowrap"
        >
          <Stamp size={12} /> +1
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {dots.map((filled, i) => (
          <div
            key={i}
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] font-bold transition ${
              filled
                ? "bg-[#9D00FF] border-[#9D00FF] text-white"
                : "border-white/20 text-transparent"
            }`}
          >
            {filled ? "✓" : i + 1}
          </div>
        ))}
      </div>
      <div className="text-[11px] text-[#A0A0A0] mt-1.5">
        {card.stamps_earned}/{card.required_stamps} {card.item_label}s
        {card.completed_count > 0 && ` · ${card.completed_count} earned lifetime`}
      </div>
    </div>
  );
}

function GrantRewardDialog({ customerId, customerName, onClose, onGranted }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [expiresDays, setExpiresDays] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/customers/${customerId}/rewards`, {
        customer_id: customerId, title: title.trim(),
        description: description.trim(),
        expires_days: expiresDays ? Number(expiresDays) : null,
      });
      toast.success(`Granted ${title} to ${customerName}`);
      onGranted();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl w-full max-w-sm p-6 tb-fade-up">
        <h2 className="text-xl font-bold mb-1">Grant Reward</h2>
        <p className="text-xs text-[#A0A0A0] mb-4">To {customerName}</p>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <input
            required
            data-testid="grant-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. 1 free cocktail"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description or terms (optional)"
            rows={2}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#9D00FF]"
          />
          <input
            type="number" min="0"
            value={expiresDays}
            onChange={(e) => setExpiresDays(e.target.value)}
            placeholder="Expires in days (optional)"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]"
          />
          <div className="flex gap-2 mt-1">
            <button type="button" onClick={onClose} className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg py-2.5 min-h-[44px] font-semibold">Cancel</button>
            <button type="submit" disabled={loading || !title.trim()} data-testid="grant-submit" className="flex-1 tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 rounded-lg py-2.5 min-h-[44px] font-semibold">
              {loading ? "…" : "Grant"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ---- Loyalty Points Card (balance + redeem + milestones) ----
export function LoyaltyPointsCard({ customer, onRefresh }) {
  const [achievements, setAchievements] = useState([]);
  const [showRedeem, setShowRedeem] = useState(false);
  const bal = customer.points_balance || 0;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/milestones/unredeemed/${customer.id}`);
      setAchievements(data || []);
    } catch {}
  }, [customer.id]);

  useEffect(() => { load(); }, [load]);

  const markRedeemed = async (aid) => {
    try {
      await api.post("/staff/milestones/mark-redeemed", { achievement_id: aid });
      toast.success("Milestone marked redeemed");
      await load();
      await onRefresh();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <>
      <div className="bg-[#1A1A1A] border border-[#9D00FF]/20 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">Loyalty Points</div>
            <div className="flex items-baseline gap-2" data-testid="customer-loyalty-balance">
              <span className="text-3xl font-black text-[#9D00FF]">🏆 {bal}</span>
              <span className="text-sm text-[#A0A0A0]">points · ₹{bal} redeemable</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LoyaltyActions
              customerId={customer.id}
              balance={bal}
              onDone={async () => { await onRefresh(); await load(); }}
            />
            <button
              onClick={() => setShowRedeem(true)}
              disabled={bal <= 0}
              data-testid="redeem-points-button"
              className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-40 text-white text-sm font-bold uppercase tracking-wider rounded-lg px-4 py-2.5"
            >
              Redeem Points
            </button>
          </div>
        </div>

        {achievements.length > 0 && (
          <div className="mt-4 flex flex-col gap-2">
            {achievements.map((a) => (
              <div key={a.id} data-testid={`milestone-banner-${a.id}`} className="bg-[#F59E0B]/10 border border-[#F59E0B]/40 rounded-lg p-3 flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-sm font-bold text-[#F59E0B]">🎉 Milestone reward pending</div>
                  <div className="text-xs text-white mt-0.5">{a.reward_description}</div>
                </div>
                <button
                  onClick={() => markRedeemed(a.id)}
                  data-testid={`milestone-mark-redeemed-${a.id}`}
                  className="bg-[#F59E0B] text-black text-xs font-bold uppercase tracking-wider rounded-lg px-3 py-2 whitespace-nowrap"
                >
                  Mark Redeemed
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {showRedeem && (
        <RedeemModal
          customer={customer}
          balance={bal}
          onClose={() => setShowRedeem(false)}
          onDone={async () => { setShowRedeem(false); await onRefresh(); await load(); }}
        />
      )}
    </>
  );
}

function RedeemModal({ customer, balance, onClose, onDone }) {
  const [points, setPoints] = useState("");
  const [billNumber, setBillNumber] = useState("");
  const [overridePin, setOverridePin] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmBlank, setConfirmBlank] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const p = Number(points);
    if (!p || p <= 0) { toast.error("Enter points to redeem"); return; }
    if (p > balance) { toast.error(`Only ${balance} points available`); return; }
    if (!/^\d{4}$/.test(overridePin)) { toast.error("Enter the 4-digit override PIN"); return; }
    // Amber warning if bill number is blank
    if (!billNumber.trim() && !confirmBlank) { setConfirmBlank(true); return; }
    setLoading(true);
    try {
      await api.post("/staff/points/redeem", {
        customer_id: customer.id,
        points: p,
        bill_number: billNumber.trim() || null,
        override_pin: overridePin,
      });
      toast.success(`Redeemed ${p} points for ${customer.name}`);
      onDone();
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-bold mb-1">Redeem Loyalty Points</h3>
        <p className="text-sm text-[#A0A0A0] mb-4">
          {customer.name} has <b className="text-[#9D00FF]">🏆 {balance} points</b> available.
        </p>

        <label className="block mb-4">
          <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5">Points to Redeem</div>
          <input
            required type="number" min="1" max={balance}
            data-testid="redeem-points-input"
            value={points}
            onChange={(e) => { setPoints(e.target.value); setConfirmBlank(false); }}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-lg focus:border-[#9D00FF]"
          />
          {Number(points) > 0 && (
            <p className="text-xs text-[#9D00FF] mt-1">{Number(points)} points = ₹{Number(points)} off the bill</p>
          )}
        </label>

        <label className="block mb-4">
          <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5">Bill / Receipt Number (recommended)</div>
          <input
            data-testid="redeem-bill-number"
            value={billNumber}
            maxLength={50}
            onChange={(e) => { setBillNumber(e.target.value); setConfirmBlank(false); }}
            placeholder="Enter the bill number being discounted"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#9D00FF]"
          />
          <p className="text-[11px] text-[#A0A0A0] mt-1">Required for audit — enter the bill on which this discount is being applied.</p>
        </label>

        <label className="block mb-4">
          <div className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5">Override PIN</div>
          <input
            required type="password" inputMode="numeric" maxLength={4}
            data-testid="redeem-override-pin"
            value={overridePin}
            onChange={(e) => { setOverridePin(e.target.value.replace(/\D/g, "")); setConfirmBlank(false); }}
            placeholder="4-digit manager PIN"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white tracking-[0.3em] focus:border-[#9D00FF]"
          />
          <p className="text-[11px] text-[#A0A0A0] mt-1">A manager PIN authorizes this redemption; failed attempts are logged and rate-limited.</p>
        </label>

        {confirmBlank && (
          <div data-testid="redeem-no-bill-warning" className="bg-[#F59E0B]/10 border border-[#F59E0B]/40 rounded-lg p-3 mb-3 text-xs text-[#F59E0B]">
            No bill number entered — are you sure? This may make disputes harder to resolve.
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={() => setConfirmBlank(false)}
                className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-1.5 text-xs font-bold text-white">Go Back</button>
              <button type="submit" disabled={loading} data-testid="redeem-continue-without-bill"
                className="bg-[#F59E0B] text-black rounded-lg px-3 py-1.5 text-xs font-bold">Continue anyway</button>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg py-2.5 font-semibold">Cancel</button>
          <button
            type="submit" disabled={loading}
            data-testid="redeem-confirm"
            className="flex-1 tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-50 rounded-lg py-2.5 font-semibold"
          >
            {loading ? "…" : "Confirm Redemption"}
          </button>
        </div>
      </form>
    </div>
  );
}

// A compact "add a date" affordance, reused for date of birth and anniversary (AA-8). field is the
// customer key patched; data-testids derive from it (dob-* preserved for existing tests).
function DatePrompt({ customerId, field, label, onSaved }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!val) return;
    setSaving(true);
    try {
      await api.patch(`/customers/${customerId}`, { [field]: val });
      toast.success(`${label} saved`);
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`${field}-prompt-btn`}
        onClick={() => setOpen(true)}
        className="text-xs text-[#9D00FF] flex items-center gap-1 mt-0.5 hover:underline"
      >
        <Calendar size={11} /> Add {label.toLowerCase()}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 mt-0.5">
      <input
        type="date"
        data-testid={`${field}-input`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="bg-[#0A0A0A] border border-white/20 rounded px-2 py-1 text-xs text-white focus:border-[#9D00FF]"
      />
      <button
        type="button"
        data-testid={`${field}-save-btn`}
        onClick={save}
        disabled={!val || saving}
        className="text-xs text-[#9D00FF] font-semibold disabled:opacity-40"
      >
        {saving ? "…" : "Save"}
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-[#A0A0A0]">Cancel</button>
    </div>
  );
}
